// Lazy Twitch clip iframe with eager preload support.

import React, { useEffect, useMemo, useState } from 'react';
import { useSystemViewportObserver } from '../../hooks';
import { Spinner } from '../ui/spinner';
import { getTwitchClipEmbedUrl } from './embed';

interface TwitchEmbedProps {
  clipUrl: string;
  width?: number;
  height?: number;
  eager?: boolean;
  interactive?: boolean;
  onLoad?: () => void;
}

const TwitchEmbed: React.FC<TwitchEmbedProps> = ({
  clipUrl,
  width = 534,
  height = 300,
  eager = false,
  interactive = true,
  onLoad,
}) => {
  const [ref, isIntersecting] = useSystemViewportObserver({
    rootMargin: '200px',
    triggerOnce: true,
  });

  const shouldLoad = eager || isIntersecting;
  const [isLoaded, setIsLoaded] = useState(false);
  const hostname =
    typeof window !== 'undefined'
      ? window.location.hostname
      : 'airheadgames.com';
  const embedUrl = useMemo(
    () => getTwitchClipEmbedUrl(clipUrl, hostname),
    [clipUrl, hostname]
  );

  useEffect(() => {
    if (!shouldLoad) {
      setIsLoaded(false);
    }
  }, [embedUrl, shouldLoad]);

  return (
    <div
      ref={ref}
      aria-hidden={interactive ? undefined : true}
      className="relative w-full overflow-hidden rounded-xl bg-black"
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {shouldLoad && (
        <iframe
          src={embedUrl}
          width={width}
          height={height}
          allowFullScreen
          tabIndex={interactive ? undefined : -1}
          title="Twitch Clip"
          className="absolute inset-0 block h-full w-full border-0 transition-opacity"
          style={{ opacity: isLoaded ? 1 : 0 }}
          onLoad={() => {
            setIsLoaded(true);
            onLoad?.();
          }}
        />
      )}
      {(!shouldLoad || !isLoaded) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black">
          <Spinner className="size-12 text-primary" />
        </div>
      )}
    </div>
  );
};

export default TwitchEmbed;
